/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

// Utility functions for common app-wide logic.
// Add reusable helper methods here for use across the project.

import APIHelper from "../api/APIHelper";
import envConfig from "../config/envConfig";

const apiHelper = new APIHelper({
  apiUrl: envConfig.backendApiUrl,
  tokenUrl: envConfig.backendTokenUrl,
  clientId: envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

/**
 * Safely get and parse a JSON value from localStorage by key.
 * Returns the full parsed object, or null if key not found or parsing fails.
 * @param {string} key - The localStorage key to fetch.
 * @returns {*} Parsed object or null if not found/error.
 */
export function getValueFromLocalStorage(key) {
  const jsonString = localStorage.getItem(key);
  if (!jsonString) return null;

  try {
    return JSON.parse(jsonString);
  } catch (error) {
    console.error(`LocalStorage parsing error for key "${key}":`, error);
    return null;
  }
}

/**
 * Handles Error response
 * @param {resp} resp 
 */
export async function handleErrorResponse(resp) {
  console.log(resp.status)
  if (resp.status === 400 || resp.status === 404) {
    const errorJson = await resp.json();
    const combinedErrorMessages = Object.entries(errorJson)
      .map(([field, messages]) => {
        if (Array.isArray(messages)) {
          return `${field}: ${messages.join(", ")}`;
        }
        return `${field}: ${String(messages)}`;
      })
      .join("\n");
    throw new Error(combinedErrorMessages);
  } else {
    throw new Error("An unexpected error occurred. Please refresh or try again later.");
  }
}

/**
 * Checks if the user is logged in by calling the backend session validation API.
 *
 * Logic:
 * - Sends a GET request to `/user/login/check` using APIHelper.
 * - Includes session cookies (`credentials: "include"`) so Django can validate the session.
 * - If the backend returns HTTP 200 → the user is authenticated.
 * - Any other status code (401, 403, 500, network error) → user is not logged in.
 *
 * This function does **not** trigger session-expired dialogs, does **not** dispatch events,
 * and does **not** rely on any localStorage data. It is intended for simple checks such as:
 *   - redirecting already logged-in users away from the sign-in page
 *   - ensuring boot-time session validation
 *
 * @returns {Promise<boolean>} True if the backend confirms the user is logged in, else false.
 */
export async function isUserLoggedIn() {
  try {
    const resp = await apiHelper.apiCall("/arcee/user/login/check", {
      method: "GET",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      suppressSessionEvent: true,
    });

    return resp.ok; // 200 → true, anything else → false
  } catch (err) {
    return false;
  }
}